import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { checksheetCopy, checksheetGetAllByYear } from 'apis/checksheetAPIs';
// import { idsConcentrationGetAll } from 'apis/adminAPIs';
// import { fetchDplUndergradProgramsByAcadPlanCode } from 'apis/dplAPIs';
import { Button } from '@asu/components-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faXmark,
  // faBuildingColumns,
} from '@fortawesome/free-solid-svg-icons';
import StaticData from './StaticData';
import { classNames } from 'utils';
import useServiceAuth from 'auth/useServiceAuth';
import { setChecksheetList } from 'state/slices/dataListsSlice';

const SearchProgram = ({ program }) => {
  const { degree, owners } = useMemo(() => {
    const degree = program.degreeDescriptionShort
      ? program.degreeDescriptionShort === 'CERT'
        ? ', Certificate'
        : `, ${program.degreeDescriptionShort}`
      : program.acadPlanType === 'MIN'
        ? ', Minor'
        : null;

    // Use Set for O(1) deduplication
    const ownerSet = new Set();
    const owners = [];
    program.owners.forEach((programOwner) => {
      if (!ownerSet.has(programOwner.collegeAcadOrg)) {
        ownerSet.add(programOwner.collegeAcadOrg);
        owners.push({
          collegeAcadOrg: programOwner.collegeAcadOrg,
          collegeDescription: programOwner.collegeDescription,
        });
      }
    });

    return { degree, owners };
  }, [program]);

  return (
    <>
      <div className="fs-6 fw-bold">
        {program.acadPlanDescription}
        {degree}
      </div>
      {program.subplan && (
        <div className="fw-bold">
          {program.subplan.description}
          <span className="subplan-tag ms-1">Subplan</span>
        </div>
      )}
      <div className="text-gray-6" style={{ fontSize: '14px' }}>
        {owners.map((owner, index) => (
          <div
            key={owner.collegeAcadOrg}
            className={classNames(index !== owners.length - 1 && 'mb-1')}
          >
            {owner.collegeDescription}
          </div>
        ))}
      </div>
      <div className="text-gray-6" style={{ fontSize: '14px' }}>
        {program.acadPlanCode}
        {program.subplan && <span> / {program.subplan.acadSubPlanCode}</span>}
      </div>
    </>
  );
};

const CopyChecksheet = ({
  checksheet,
  programList,
  // idsList,
  handleClose,
}) => {
  const { getAccessToken } = useServiceAuth();
  const dispatch = useDispatch();

  const [checksheets, setChecksheets] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [currentChecksheetDegree, setCurrentChecksheetDegree] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProgramDegree, setSelectedProgramDegree] = useState(null);
  const [toProgramYear, setToProgramYear] = useState('2023');
  const [error, setError] = useState(null);

  const year = useSelector((state) => state.settings.year);
  const collegeList = useSelector((state) => state.dataLists.collegeList);

  const yearObject = StaticData.yearList.reduce((acc, curr) => {
    acc[curr.value] = curr.label;
    return acc;
  }, {});

  // Create lookup maps for O(1) access
  const checksheetLookup = useMemo(() => {
    const lookup = new Map();
    checksheets.forEach((checksheet) => {
      const [, , planCode, subPlanCode] = checksheet.id.split('*');
      const key = `${planCode}:${subPlanCode}`;
      lookup.set(key, checksheet);
    });
    return lookup;
  }, [checksheets]);

  // Fetch IDS concentrations for the selected catalog year
  // const {
  //   data: idsConcentrationsForYear,
  //   isSuccess: idsConcentrationsIsSuccess,
  // } = useQuery({
  //   queryKey: ['idsConcentrations', toProgramYear],
  //   queryFn: async () => {
  //     const token = await getAccessToken();
  //     return idsConcentrationGetAll({ year: toProgramYear, token });
  //   },
  //   enabled: !!toProgramYear,
  // });

  // Fetch IDS program data to get descriptions
  // const { data: idsProgramData, isSuccess: idsProgramIsSuccess } = useQuery({
  //   queryKey: ['program', 'LSBISBIS'],
  //   queryFn: () => {
  //     return fetchDplUndergradProgramsByAcadPlanCode('LSBISBIS');
  //   },
  // });

  // Optimize search filtering with single pass
  const filteredResults = useMemo(() => {
    if (!searchString) return { programs: [], ids: [] };

    const searchLower = searchString.toLowerCase();

    // Filter programs that don't have existing checksheets
    const programs = programList.filter((program) => {
      const key = `${program.acadPlanCode}:${program.subplan?.acadSubPlanCode || 'null'}`;
      if (checksheetLookup.has(key)) return false; // Exclude existing checksheets

      return (
        program.acadPlanCode.toLowerCase().includes(searchLower) ||
        program.acadPlanDescription.toLowerCase().includes(searchLower) ||
        (program.subplan &&
          (program.subplan.acadSubPlanCode
            .toLowerCase()
            .includes(searchLower) ||
            program.subplan.description.toLowerCase().includes(searchLower)))
      );
    });

    // Use year-specific IDS data if available, otherwise fall back to prop
    // const currentIdsList =
    //   idsConcentrationsIsSuccess && idsProgramIsSuccess
    //     ? // Transform the API data to match the expected format
    //       idsProgramData.subplans
    //         .filter((subplan) => subplan.acadSubplanTypeCode === 'CON')
    //         .map((program) => ({
    //           acadPlanCode: 'LSBISBIS',
    //           acadPlanDescription: program.description,
    //           acadPlanType: program.acadSubplanTypeCode,
    //           owner: idsConcentrationsForYear[program.acadSubPlanCode],
    //           subplan: { acadSubPlanCode: program.acadSubPlanCode },
    //         }))
    //         .filter((conc) => conc.owner) // Only include concentrations with owners
    //     : idsList;

    // Filter IDS concentrations that don't have checksheets
    // const ids = currentIdsList.filter((conc) => {
    //   if (!conc.owner || conc.owner.college === 'null') return false;

    //   const key = `LSBISBIS:${conc.subplan.acadSubPlanCode}`;
    //   if (checksheetLookup.has(key)) return false; // Exclude existing checksheets

    //   return (
    //     conc.acadPlanCode.toLowerCase().includes(searchLower) ||
    //     conc.acadPlanDescription.toLowerCase().includes(searchLower) ||
    //     conc.subplan.acadSubPlanCode.toLowerCase().includes(searchLower)
    //   );
    // });

    return {
      programs,
      // ids
    };
  }, [
    searchString,
    programList,
    checksheetLookup,
    // idsConcentrationsForYear,
    // idsConcentrationsIsSuccess,
    // idsProgramData,
    // idsProgramIsSuccess,
    // idsList,
  ]);

  let owners = [];
  let mainOwner = {};
  if (selectedProgram && !selectedProgram.owner) {
    if (selectedProgram.owners.length > 0) {
      let highestPercent = 0;
      const ownerSet = new Set();
      selectedProgram.owners.forEach((programOwner) => {
        if (!ownerSet.has(programOwner.collegeAcadOrg)) {
          ownerSet.add(programOwner.collegeAcadOrg);
          owners.push({
            collegeAcadOrg: programOwner.collegeAcadOrg,
            collegeDescription: programOwner.collegeDescription,
          });
        }

        if (programOwner.percentOwned > highestPercent) {
          highestPercent = programOwner.percentOwned;
          mainOwner = programOwner;
        }
      });
    }
  }

  const {
    data: checksheetData,
    error: getChecksheetError,
    isError: getChecksheetIsError,
    isSuccess: getChecksheetIsSuccess,
  } = useQuery({
    queryKey: ['checksheets', toProgramYear],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGetAllByYear({ year: toProgramYear, token });
    },
    enabled: !!toProgramYear,
  });

  const { mutate: getAllChecksheets } = useMutation({
    mutationFn: checksheetGetAllByYear,
    onSuccess: async (data) => {
      const next = [];

      data.forEach((checksheet) => {
        const idYear = checksheet['sk'].S.split('*')[0];
        let lockedBy = {};

        if (!!checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        const obj = {
          id: checksheet['sk'].S,
          programType: checksheet['programType'].S,
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          lockedBy: lockedBy,
          proposed: checksheet['proposed'] ? true : false,
        };

        next.push(obj);
      });

      dispatch(setChecksheetList([...next]));
    },
  });

  const { mutate: copyChecksheet } = useMutation({
    mutationFn: checksheetCopy,
    onSuccess: async (data, { token }) => {
      getAllChecksheets({ year, token });
    },
  });

  const handleCopyChecksheet = async () => {
    if (selectedProgram) {
      let jsonData;

      if (selectedProgram.owner) {
        // Handle IDS concentration
        jsonData = {
          checksheetfrom: checksheet.checksheet.id,
          acadplanto: selectedProgram.acadPlanCode,
          acadsubplanto: selectedProgram.subplan?.acadSubPlanCode,
          collegeto: selectedProgram.owner.college,
          departmentto: selectedProgram.owner.department,
          nameto: selectedProgram.acadPlanDescription,
          programtypeto: 'CONC',
          yearto: toProgramYear,
        };
      } else {
        // Handle regular program
        jsonData = {
          checksheetfrom: checksheet.checksheet.id,
          acadplanto: selectedProgram.acadPlanCode,
          acadsubplanto: selectedProgram.subplan
            ? selectedProgram.subplan.acadSubPlanCode
            : null,
          collegeto: mainOwner.collegeAcadOrg,
          departmentto: mainOwner.departmentAcadOrg,
          nameto: selectedProgram.acadPlanDescription + selectedProgramDegree,
          programtypeto:
            // selectedProgram.acadPlanCode === 'LSBISBIS' &&
            // selectedProgram.subplan
            //   ? 'IDS'
            //   :
            selectedProgram.degreeDescriptionShort ??
            selectedProgram.acadPlanType,
          yearto: toProgramYear,
        };
      }

      console.log('JSON: ', jsonData);

      const token = await getAccessToken();
      copyChecksheet({ jsonData: jsonData, token: token });

      handleClose();
    } else {
      alert('Program not selected');
    }
  };

  const handleSelectToProgramYear = (e) => {
    setToProgramYear(e.target.value);
  };

  const handleSelectProgram = useCallback((program) => {
    setSelectedProgram(program);
  }, []);

  // const handleSelectIdsConcentration = useCallback((concentration) => {
  //   setSelectedProgram(concentration);
  // }, []);

  useEffect(() => {
    if (checksheet && checksheet.program) {
      const dds = checksheet.program.degreeDescriptionShort
        ? checksheet.program.degreeDescriptionShort === 'CERT'
          ? ', Certificate'
          : `, ${checksheet.program.degreeDescriptionShort}`
        : checksheet.program.acadPlanType === 'MIN'
          ? ', Minor'
          : null;

      setCurrentChecksheetDegree(dds);
    }
  }, [checksheet]);

  useEffect(() => {
    if (selectedProgram && !selectedProgram.owner) {
      const dds = selectedProgram.degreeDescriptionShort
        ? selectedProgram.degreeDescriptionShort === 'CERT'
          ? ', Certificate'
          : `, ${selectedProgram.degreeDescriptionShort}`
        : selectedProgram.acadPlanType === 'MIN'
          ? ', Minor'
          : null;

      setSelectedProgramDegree(dds);
    }
  }, [selectedProgram]);

  useEffect(() => {
    if (getChecksheetIsError) setError(getChecksheetError.message);

    if (getChecksheetIsSuccess) {
      const next = [];

      checksheetData.forEach((checksheet) => {
        const idYear = checksheet['sk'].S.split('*')[0];
        let lockedBy = {};

        if (!!checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        const obj = {
          id: checksheet['sk'].S,
          programType: checksheet['programType'].S,
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          lockedBy: lockedBy,
          proposed: checksheet['proposed'] ? true : false,
        };

        next.push(obj);
      });

      setChecksheets([...next]);
    }
  }, [
    checksheetData,
    getChecksheetError,
    getChecksheetIsError,
    getChecksheetIsSuccess,
  ]);

  if (error) return <div>Error: {error}</div>;

  if (!checksheet || !checksheet.program) return <div>Loading...</div>;

  return (
    <div className="p-6" style={{ width: '1182px' }}>
      <div className="fs-4 fw-bold mb-4">Copy checksheet</div>
      <div className="row justify-content-between mb-9">
        <div className="col-5">
          <div className="fs-5 fw-bold mb-2">Copy from</div>
          <div className="border border-gray-5 w-100 mb-3">
            <div className="border-start border-8 border-maroon ps-2 pe-3 py-3">
              <div className="fs-4 fw-bold">
                {checksheet.program.acadPlanDescription}
                {currentChecksheetDegree}
              </div>

              {checksheet?.program?.subplan && (
                // checksheet?.program?.acadPlanCode !== 'LSBISBIS' &&
                <div className="fw-bold">
                  {checksheet.program.subplan.description}
                  <span className="subplan-tag ms-1">Subplan</span>
                </div>
              )}
              {/* {checksheet?.program?.acadPlanCode === 'LSBISBIS' &&
                checksheet?.program?.subplan && (
                  <div className="fw-bold">
                    <span className="subplan-tag">
                      Interdisciplinary studies
                    </span>
                  </div>
                )} */}
              <div>
                {checksheet.college.description ??
                  checksheet.college.collegeDescription}
              </div>
              <div>
                {checksheet.program.acadPlanCode}
                {checksheet.program.subplan && (
                  <span> / {checksheet.program.subplan.acadSubPlanCode}</span>
                )}
              </div>
              <div>{yearObject[year]}</div>
            </div>
          </div>
        </div>
        <div className="col-2 d-flex justify-content-center">
          <div
            className="bg-gray-2 lh-1 mt-10 rounded-circle"
            style={{
              height: 'fit-content',
              width: 'fit-content',
              padding: '16px 17px',
            }}
          >
            <FontAwesomeIcon
              icon={faArrowRight}
              style={{ width: '26px', height: '27px' }}
            />
          </div>
        </div>
        <div className="col-5">
          <div className="fs-5 fw-bold mb-2">To program</div>
          {!selectedProgram ? (
            <>
              <div className="mb-2">
                <div className="fw-bold">Catalog year</div>
                <select
                  value={toProgramYear}
                  onChange={handleSelectToProgramYear}
                  styles={{
                    width: '150px',
                  }}
                  className="text-gray-7 p-1"
                >
                  {StaticData.yearList.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fw-bold">Search programs</div>
              <div className="position-relative">
                <input
                  placeholder="Search by program title or plan code"
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                  className="border border-gray-5 ps-2 pe-6 py-1 w-100"
                />
                {searchString && (
                  <div
                    className="position-absolute border border-top-0 border-gray-5 w-100 overflow-y-auto"
                    style={{ maxHeight: '198px', zIndex: '1' }}
                  >
                    {filteredResults.programs.map((program, index, array) => (
                      <div
                        key={program.acadPlanCode + index}
                        className={classNames(
                          index !== array.length - 1
                            ? // || filteredResults.ids.length > 0
                              'border-bottom'
                            : '',
                          'border-gray-5 p-2 bg-white'
                        )}
                        onClick={() => handleSelectProgram(program)}
                        role="button"
                      >
                        <SearchProgram
                          program={program}
                          checksheet={checksheet}
                        />
                      </div>
                    ))}
                    {/* {filteredResults.ids.map((conc, index, array) => (
                      <div
                        key={conc.subplan?.acadSubPlanCode || index}
                        className={classNames(
                          index !== array.length - 1 && 'border-bottom',
                          'border-gray-5 p-2 bg-white'
                        )}
                        onClick={() => handleSelectIdsConcentration(conc)}
                        role="button"
                      >
                        <div className="fs-6 fw-bold">
                          {conc.acadPlanDescription}
                        </div>
                        <div
                          className="text-gray-6"
                          style={{ fontSize: '14px' }}
                        >
                          {conc.subplan?.acadSubPlanCode}
                        </div>
                        <div className="d-flex gap-1 align-items-center">
                          <FontAwesomeIcon
                            icon={faBuildingColumns}
                            className="text-gray-6"
                            width={'16px'}
                          />
                          {conc?.owner?.college &&
                            !!collegeList?.[conc.owner.college] &&
                            collegeList[conc.owner.college].description}
                        </div>
                      </div>
                    ))} */}
                    {filteredResults.programs.length === 0 && (
                      // filteredResults.ids.length === 0 &&
                      <div className="p-2 fst-italic">No programs found</div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="border border-gray-5 w-100 mb-3">
                <div className="border-start border-8 border-gold ps-2 pe-3 py-3">
                  <div className="d-flex justify-content-between">
                    <div className="fs-4 fw-bold">
                      {selectedProgram.owner
                        ? selectedProgram.acadPlanDescription
                        : selectedProgram.acadPlanDescription +
                          selectedProgramDegree}
                    </div>
                    <div
                      className="bg-gray-2 lh-1 rounded-circle ms-1 mb-1"
                      style={{ height: 'min-content', padding: '3px 6px 5px' }}
                      onClick={() => setSelectedProgram(null)}
                      role="button"
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        style={{ height: '12px', width: '12px' }}
                      />
                    </div>
                  </div>
                  {selectedProgram.subplan && (
                    // selectedProgram.acadPlanCode !== 'LSBISBIS' &&
                    <div className="fw-bold">
                      {selectedProgram.subplan.description}
                      <span className="subplan-tag ms-1">Subplan</span>
                    </div>
                  )}
                  {/* {selectedProgram.acadPlanCode === 'LSBISBIS' &&
                    selectedProgram.subplan && (
                      <div className="fw-bold">
                        <span className="subplan-tag">
                          Interdisciplinary studies
                        </span>
                      </div>
                    )} */}
                  <div>
                    {selectedProgram.owner
                      ? collegeList[selectedProgram.owner.college]?.description
                      : owners.map((owner, index) => (
                          <div
                            key={owner.collegeAcadOrg}
                            className={classNames(
                              index !== owners.length - 1 && 'mb-1'
                            )}
                          >
                            {owner.collegeDescription}
                          </div>
                        ))}
                  </div>
                  <div>
                    {selectedProgram.owner
                      ? selectedProgram.subplan?.acadSubPlanCode
                      : selectedProgram.acadPlanCode +
                        (selectedProgram.subplan
                          ? ` / ${selectedProgram.subplan.acadSubPlanCode}`
                          : '')}
                  </div>
                  <div>{yearObject[toProgramYear]}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <Button
          color="dark"
          label="Cancel"
          size="medium"
          onClick={handleClose}
        />
        <Button
          color="gold"
          label="Copy checksheet"
          size="medium"
          onClick={handleCopyChecksheet}
          disabled={!selectedProgram}
        />
      </div>
    </div>
  );
};

export default CopyChecksheet;
